import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import { toastStyle } from "../../Containers/continerUtilityFunctions";
import EditorComponent from './EditorComponent';

import {
	Autocomplete, Box, Button, Typography, FormHelperText, Chip, Stack, IconButton, TextField,
	Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteIcon from '@mui/icons-material/Delete';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { setMonthlyCostStatusEmailFormData, setMonthlyCostStatusEmailFormFiles, setMonthlyCostStatusEmailFormSourceKey, mergeMonthlyCostStatusData } from '../../../../store/slices/projectSlice';
import { draftSourceKey } from './costStatusDraftSync';
import { autoSavePHDDraft } from '../../Containers/api';
import '../../static/header-styles.css'
import { renderCostValue } from '../utilityFunctions';
import MonthlyCostStatusFinancialSummary from './MonthlyCostStatusFinancialSummary';
import { currentOverUnderBillings, priorOverUnderBillings } from './overUnderBillings';
import {
	PHD_NARRATIVE_FIELD_IDS,
	narrativeFieldsForStorage,
	narrativeFieldsForCompare,
	narrativeFieldsEqual,
	hasMeaningfulContent,
	saveDraft as saveLocalDraft,
	loadDraft as loadLocalDraft,
	clearDraft as clearLocalDraft,
	shouldRestoreLocalDraft,
} from '../../Containers/phdDraftStorage';

const SERVER_AUTOSAVE_INTERVAL_MS = 10_000;

const useReduxSelectors = () => {
    const { settingsData, monthlyCostStatusData, monthlyCostStatusEmailFormData, monthlyCostStatusEmailFormFiles, monthlyCostStatusEmailFormSourceKey, budgetData, selectedProjectId, invoiceData, users } = useSelector(
        state => state.project
    );
    const hubDomain = useSelector((state) => state.global.hubDomain);
    return { settingsData, monthlyCostStatusData, monthlyCostStatusEmailFormData, monthlyCostStatusEmailFormFiles, monthlyCostStatusEmailFormSourceKey, budgetData, hubDomain, selectedProjectId, invoiceData, users };
};

export default function MonthlyCostStatusEmailDrafter({ selectedSnapshot, draftReviewers, setDraftReviewers }) {
    const dispatch = useDispatch();
    const {
        settingsData,
        monthlyCostStatusData,
        monthlyCostStatusEmailFormData,
        monthlyCostStatusEmailFormFiles,
        monthlyCostStatusEmailFormSourceKey,
        budgetData,
        hubDomain,
        selectedProjectId,
		invoiceData,
		users
    } = useReduxSelectors();

	const fields = React.useMemo(() => [
		{
			id: 'financial_summary',
			label: 'Financial Summary Comments',
			defaultValue: '',
			helperText: 'Provide additional context on the financial summary metrics above including change in Profit and Contingency.',
		},
		{
			id: 'billing_summary',
			label: 'Billing Summary Comments',
			defaultValue: '',
			helperText: 'Summarize the billing status and any discrepancies.',
		},
		{
			id: 'permitting_and_design',
			label: 'Summary of Permitting/Design Progress',
			defaultValue: '',
			helperText: '',
		},
		{
			id: 'buyout_progress',
			label: 'Buyout Progress',
			defaultValue: '',
			helperText: '',
		},
		{
			id: 'construction_progress',
			label: 'Construction/Schedule Progress',
			defaultValue: '',
			helperText: 'Summary of work completed in past 30 days. Mention any schedule risks and provide what % complete this project will be at year end.',
		},
		{
			id: 'project_risk',
			label: 'Project Risk',
			defaultValue: '',
			helperText: 'Detailed description of project risks for costs, schedule, quality, relationships, etc.',
		},
		{
			id: 'raving_fans',
			label: 'Raving Fans',
			defaultValue: '',
			helperText: 'Plans for providing a raving experience to the client for the remainder of the project; Summary of raving fan experiences provided to the client so far; Update on remaining budget left for Raving Fans.',
		},
		{
			id: 'additional_comments',
			label: 'Additional Comments',
			defaultValue: '',
			helperText: 'Add any additional comments or notes here.',
		},
	], []);

	const tableData = React.useMemo(() => {

		const current_month_snapshots_data = settingsData?.current_month_snapshots_data[selectedSnapshot] || {};
		let penultimate_snapshots_data = budgetData?.['penultimate_contract_summary']
		const formatBillings = (amount) => (amount === null ? '--' : amount.toFixed(2));
		const current_over_under_billings = formatBillings(currentOverUnderBillings(current_month_snapshots_data, invoiceData));
		const penultimate_over_under_billings = formatBillings(priorOverUnderBillings(penultimate_snapshots_data, invoiceData));

		// LS and GMP Contingency Calc
		const current_cost = current_month_snapshots_data?.contract_structure?.Cost || {};
		const current_lsContingencyCost = Object.keys(current_cost).reduce((sum, key) => {
			if (key.startsWith('LS-')) {
				return sum + (parseFloat(current_cost[key]) || 0);
			}
			return sum;
		}, 0);
		const current_gmpContingencyCost = Object.keys(current_cost).reduce((sum, key) => {
			if (key.startsWith('GMP-')||(key.startsWith('GMP/COW'))) {
				return sum + (parseFloat(current_cost[key]) || 0);
			}
			return sum;
		}, 0);

		const penultimate_cost = penultimate_snapshots_data?.current_contract_summary?.contract_structure?.Cost || {};
		const penultimate_lsContingencyCost = Object.keys(penultimate_cost).reduce((sum, key) => {
			if (key.startsWith('LS-')) {
				return sum + (parseFloat(penultimate_cost[key]) || 0);
			}
			return sum;
		}, 0);
		const penultimate_gmpContingencyCost = Object.keys(penultimate_cost).reduce((sum, key) => {
			if (key.startsWith('GMP-')) {
				return sum + (parseFloat(penultimate_cost[key]) || 0);
			}
			return sum;
		}, 0);

		let rawTableData = [
			// Section 1: Contract
			{
				id: 3,
				title: 'Projected Contract Amount',
				value: Number(current_month_snapshots_data?.revised_contract_value || 0).toFixed(2),
				old_value: Number(penultimate_snapshots_data?.revised_contract_value || 0).toFixed(2),
				value_percent: '--',
				old_value_percent: '--',
				sectionEnd: true,
			},
			// Section 2: Profit
			{
				id: 8,
				title: 'Profit',
				value: Number(current_month_snapshots_data?.projected_over_under).toFixed(2),
				old_value: Number(penultimate_snapshots_data?.projected_over_under).toFixed(2),
				value_percent: Number(current_month_snapshots_data?.projected_over_under/current_month_snapshots_data?.revised_contract_value*100).toFixed(2),
				old_value_percent: Number(penultimate_snapshots_data?.projected_over_under/penultimate_snapshots_data?.revised_contract_value*100).toFixed(2),
				sectionEnd: true,
			},
			// Section 3: Contingency
			{
				id: 5,
				title: 'Total Contingency',
				value: Number(current_month_snapshots_data?.contingency_data?.contingency || 0).toFixed(2),
				old_value: Number(penultimate_snapshots_data?.contingency_data?.contingency || 0).toFixed(2),
				value_percent: current_month_snapshots_data?.revised_contract_value && Number(current_month_snapshots_data?.revised_contract_value) !== 0
					? (
						(Number(current_month_snapshots_data?.contingency_data?.contingency || 0) /
							Number(current_month_snapshots_data?.revised_contract_value)) *
						100
					).toFixed(2)
					: '--',
				old_value_percent: penultimate_snapshots_data?.revised_contract_value && Number(penultimate_snapshots_data?.revised_contract_value) !== 0
					? (
						(Number(penultimate_snapshots_data?.contingency_data?.contingency || 0) /
							Number(penultimate_snapshots_data?.revised_contract_value)) *
						100
					).toFixed(2)
					: '--'
			},
			{
				id: 6,
				title: 'LS Contingency',
				value: Number(current_month_snapshots_data?.contingency_data?.ls_contingency || 0).toFixed(2),
				old_value: Number(penultimate_snapshots_data?.contingency_data?.ls_contingency || 0).toFixed(2),
				value_percent: current_month_snapshots_data?.revised_contract_value && Number(current_month_snapshots_data?.revised_contract_value) !== 0
					? (Number(current_month_snapshots_data?.contingency_data?.ls_contingency || 0) /
							Number(current_month_snapshots_data?.revised_contract_value) *
						100
					).toFixed(2)
					: '--',
				old_value_percent: penultimate_snapshots_data?.revised_contract_value && Number(penultimate_snapshots_data?.revised_contract_value) !== 0
					? (
						(Number(penultimate_snapshots_data?.contingency_data?.ls_contingency || 0) /
							Number(penultimate_snapshots_data?.revised_contract_value)) *
						100
					).toFixed(2)
					: '--',
				indented: true,
			},
			{
				id: 7,
				title: 'GMP Contingency',
				value: Number(current_month_snapshots_data?.contingency_data?.gmp_contingency || 0).toFixed(2),
				old_value: Number(penultimate_snapshots_data?.contingency_data?.gmp_contingency || 0).toFixed(2),
				value_percent: current_month_snapshots_data?.revised_contract_value && Number(current_month_snapshots_data?.revised_contract_value) !== 0
					? (
						(Number(current_month_snapshots_data?.contingency_data?.gmp_contingency || 0) /
							Number(current_month_snapshots_data?.revised_contract_value)) *
						100
					).toFixed(2)
					: '--',
				old_value_percent: penultimate_snapshots_data?.revised_contract_value && Number(penultimate_snapshots_data?.revised_contract_value) !== 0
					? (
						(Number(penultimate_snapshots_data?.contingency_data?.gmp_contingency || 0) /
							Number(penultimate_snapshots_data?.revised_contract_value)) *
						100
					).toFixed(2)
					: '--',
				indented: true,
				sectionEnd: true,
			},
			// Section 4: Changes
			{
				id: 1,
				title: 'Current Contract Amount',
				value: Number(current_month_snapshots_data?.original_contract_value + current_month_snapshots_data?.approved_pcos_pccos).toFixed(2),
				old_value: Number(penultimate_snapshots_data?.original_contract_value + penultimate_snapshots_data?.approved_pcos_pccos).toFixed(2),
				value_percent: '--',
				old_value_percent: '--',
			},
			{
				id: 2,
				title: 'Potential Changes',
				value: Number(current_month_snapshots_data?.open_pcos + current_month_snapshots_data?.change_event_revenue).toFixed(2),
				old_value: Number(penultimate_snapshots_data?.open_pcos + penultimate_snapshots_data?.change_event_revenue).toFixed(2),
				value_percent: '--',
				old_value_percent: '--',
				sectionEnd: true,
			},
			// Section 5: Billings
			{
				id: 9,
				title: 'Over/Under Billings (Cumulative Invoiced - Contract Amount x % Complete)',
				value: current_over_under_billings || '--',
				old_value: penultimate_over_under_billings || '--',
				value_percent: '--',
				old_value_percent: '--',
			}
		];

		// Hide contingency rows when those attributes aren't in use on the job (ENG-420)
		// Show LS/GMP sub-rows only when BOTH types exist on the project (ENG-1127)
		const hasLsKeys = Object.keys(current_cost).some(k => k.startsWith('LS-')) ||
			Object.keys(penultimate_cost).some(k => k.startsWith('LS-'));
		const hasGmpKeys = Object.keys(current_cost).some(k => k.startsWith('GMP-') || k.startsWith('GMP/COW')) ||
			Object.keys(penultimate_cost).some(k => k.startsWith('GMP-') || k.startsWith('GMP/COW'));
		const hasBothContingencyTypes = hasLsKeys && hasGmpKeys;

		rawTableData = rawTableData.filter(row => {
			if (row.title === 'LS Contingency' && !hasBothContingencyTypes) return false;
			if (row.title === 'GMP Contingency' && !hasBothContingencyTypes) return false;
			if (row.title === 'Total Contingency' && !hasLsKeys && !hasGmpKeys) return false;
			return true;
		});

		// Get Month over month percentage change between old_value and value
		rawTableData = rawTableData.map(row => {
			let mom_change_amount = 0;
			let mom_change_percent = '--';
			let arrow_amount = null;
			let arrow_percent = null;

			mom_change_amount = (row.old_value && row.value && Math.abs(row.old_value) > 1) ? (row.value - row.old_value).toFixed(2) : '--';
			if (row.title.includes('Contingency') || row.title === 'Profit') {
				if (Number.isFinite(parseFloat(row.value_percent)) && Number.isFinite(parseFloat(row.old_value_percent))) {
					mom_change_percent = (row.value_percent - row.old_value_percent).toFixed(1)
				} else {
					mom_change_percent = '--';
				}
			} else {
				mom_change_percent = (row.old_value && row.value && Math.abs(row.old_value) > 1) ? ((mom_change_amount / row.old_value) * 100).toFixed(1) : '--';
			}

			// Helper function to determine arrow color based on value
			const getArrowColor = (value) => {
				if (!isNaN(parseFloat(value)) && parseFloat(value) > 0) return 'green';
				if (!isNaN(parseFloat(value)) && parseFloat(value) < 0) return 'red';
				return null;
			};

			// Set arrow colors (null for Over/Under Billings)
			const isOverUnderBilling = row.title.includes('Over/Under Billings');
			arrow_percent = isOverUnderBilling ? null : getArrowColor(mom_change_percent);
			arrow_amount = isOverUnderBilling ? null : getArrowColor(mom_change_amount);
			

			return {
				...row,
				mom_change_amount: mom_change_amount,
				mom_change_percent: mom_change_percent,
				arrow_amount: arrow_amount,
				arrow_percent: arrow_percent
			};
		});

		// Apply renderCostValue on all value 
		return rawTableData.map(row => ({
			...row,
			value: row.value !== '--' ? renderCostValue(row.value): '--',
			value_percent: row.value_percent !== '--' ? `${row.value_percent}%` : '--',
			mom_change_amount: row.mom_change_amount !== '--' ? renderCostValue(row.mom_change_amount): '--',
			mom_change_percent: row.mom_change_percent !== '--' ? `${row.mom_change_percent}%` : '--',
		}));
	// Add all dependencies used inside this function
	}, [settingsData, selectedSnapshot, budgetData]);


	// Local draft: mirror the 8 narrative fields to localStorage so PMs
	// don't lose work when the modal closes, the page refreshes, the session
	// times out, or the browser crashes. The key requires a real month — if
	// month hasn't loaded yet, save/load silently no-op to avoid writing under
	// a temporary key that the later hydrate pass would never find.
	const costStatusMonth = monthlyCostStatusData?.month || null;
	const isSubmitted = monthlyCostStatusData?.cost_status === 'Submitted';
	const draftKeyParts = useMemo(() => ({
		projectId: selectedProjectId,
		month: costStatusMonth,
	}), [selectedProjectId, costStatusMonth]);

	const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
	const [autoSaveStatus, setAutoSaveStatus] = useState(null);
	const [hasAutosaved, setHasAutosaved] = useState(false);
	const autoSaveInFlightRef = useRef(false);

	// This component unmounts behind the spinner on every save, so refill the
	// form only for a new server copy; otherwise a failed save wipes the PM's
	// text. On a new copy, a newer local draft still wins over the server text.
	useEffect(() => {
		if (!monthlyCostStatusData) return;
		const sourceKey = draftSourceKey(selectedProjectId, monthlyCostStatusData);
		if (sourceKey === monthlyCostStatusEmailFormSourceKey && monthlyCostStatusEmailFormData) {
			dispatch(setMonthlyCostStatusEmailFormData({ ...monthlyCostStatusEmailFormData, financial_summary_table: tableData }));
			return;
		}
		const initialState = Object.fromEntries(
			fields.map(field => [
				field.id,
				monthlyCostStatusData?.cost_status_data?.[field.id] ?? field.defaultValue ?? ''
			])
		);
		initialState['financial_summary_table'] = tableData;

		const localDraft = loadLocalDraft(draftKeyParts);
		const restore = !isSubmitted && shouldRestoreLocalDraft({
			localDraft,
			serverFields: monthlyCostStatusData?.cost_status_data,
			serverLastUpdatedAt: monthlyCostStatusData?.last_updated_at,
		});
		if (restore) {
			for (const id of PHD_NARRATIVE_FIELD_IDS) {
				initialState[id] = localDraft.fields[id];
			}
			toast.success('Restored unsaved changes from this browser.', toastStyle());
		}

		dispatch(setMonthlyCostStatusEmailFormData(initialState));
		dispatch(setMonthlyCostStatusEmailFormFiles(monthlyCostStatusData?.files || []));
		dispatch(setMonthlyCostStatusEmailFormSourceKey(sourceKey));
	}, [monthlyCostStatusData, selectedSnapshot]);

	// Normalized views for dirty-checking. Both sides are run through
	// narrativeFieldsForCompare so that a freshly hydrated form (empty
	// strings in Redux) compares equal to an empty server response.
	const currentNarrativeFields = useMemo(
		() => narrativeFieldsForStorage(monthlyCostStatusEmailFormData),
		[monthlyCostStatusEmailFormData]
	);
	const isDirty = useMemo(
		() => !narrativeFieldsEqual(monthlyCostStatusEmailFormData, monthlyCostStatusData?.cost_status_data),
		[monthlyCostStatusEmailFormData, monthlyCostStatusData]
	);

	// Written on every change, not debounced: closing the form unmounts this
	// component, and a pending timer would drop the last words typed.
	useEffect(() => {
		if (!selectedProjectId || !costStatusMonth) return;
		if (isDirty) {
			// The full 8-field object, empty strings included, so a field the
			// PM cleared stays cleared on restore.
			saveLocalDraft({ ...draftKeyParts, fields: currentNarrativeFields });
		} else {
			// Back in step with the server copy; a stale draft would re-restore.
			clearLocalDraft(draftKeyParts);
		}
	}, [currentNarrativeFields, isDirty, draftKeyParts, selectedProjectId, costStatusMonth]);

	// The interval reads the latest form through refs so typing doesn't restart
	// it; otherwise a PM who never pauses for 10s never gets a server save.
	const latestAutosaveRef = useRef({});
	latestAutosaveRef.current = { isDirty, isSubmitted, currentNarrativeFields, monthlyCostStatusData };

	useEffect(() => {
		if (!selectedProjectId || !costStatusMonth) return undefined;

		const intervalId = setInterval(async () => {
			const { isDirty: dirty, isSubmitted: submitted, currentNarrativeFields: sent } = latestAutosaveRef.current;
			if (!dirty || submitted || autoSaveInFlightRef.current) return;

			autoSaveInFlightRef.current = true;
			setAutoSaveStatus('saving');

			try {
				const response = await autoSavePHDDraft(selectedProjectId, sent);
				if (!response?.success) throw new Error(response?.error || 'Autosave failed');

				const server = latestAutosaveRef.current.monthlyCostStatusData;
				const saved = {
					cost_status_data: { ...(server?.cost_status_data || {}), ...sent },
					last_updated_at: response.updated_at,
					last_updated_by: response.updated_by,
				};
				// Claim the new server copy as the form's source first, so the
				// hydrate effect keeps anything typed while this request was out.
				dispatch(setMonthlyCostStatusEmailFormSourceKey(draftSourceKey(selectedProjectId, { ...server, ...saved })));
				dispatch(mergeMonthlyCostStatusData(saved));
				setHasAutosaved(true);
				setAutoSaveStatus('saved');
				setTimeout(() => {
					setAutoSaveStatus((prev) => (prev === 'saved' ? null : prev));
				}, 3000);
			} catch (err) {
				console.warn('[PHD autosave] Failed:', err);
				setAutoSaveStatus('error');
				setTimeout(() => {
					setAutoSaveStatus((prev) => (prev === 'error' ? null : prev));
				}, 5000);
			} finally {
				autoSaveInFlightRef.current = false;
			}
		}, SERVER_AUTOSAVE_INTERVAL_MS);

		return () => clearInterval(intervalId);
	}, [selectedProjectId, costStatusMonth, dispatch]);

	// Best-effort warning before the iframe unloads. Note: this fires
	// when the iframe itself navigates/reloads (e.g. via postMessage
	// from the parent), but NOT when the user closes the parent tab —
	// the parent phd.html window owns that event. The real protection
	// against tab-close data loss is the localStorage restore above;
	// next time the user opens the form, their text comes back.
	useEffect(() => {
		if (!isDirty) return undefined;
		const handler = (e) => {
			e.preventDefault();
			e.returnValue = '';
			return '';
		};
		window.addEventListener('beforeunload', handler);
		return () => window.removeEventListener('beforeunload', handler);
	}, [isDirty]);

	const handleClearAll = () => {
		const cleared = Object.fromEntries(
			fields.map(field => [field.id, field.defaultValue ?? ''])
		);
		cleared['financial_summary_table'] = tableData;
		dispatch(setMonthlyCostStatusEmailFormData(cleared));
		clearLocalDraft(draftKeyParts);
		setClearConfirmOpen(false);
		toast.success('Cost status text cleared.', toastStyle());
	};

	const clearButtonEnabled = hasMeaningfulContent(monthlyCostStatusEmailFormData);

	const emailRecipients = (settingsData?.monthly_cost_status_data?.cost_status_email_recipients || []).map((recipient) => recipient.name);

	// FILE STUFF
	const handleFileChange = (e) => {
		const maxFileSize = 10 * 1024 * 1024; // 10 MB in bytes
		const allowedFileTypes = [
			'image/jpeg', 'image/png', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // Word files
			'text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // CSV and Excel files
			'application/vnd.ms-project', 'application/vnd.openxmlformats-officedocument.project', // Microsoft Project files
			'application/x-msproject', 'application/msproject', 'application/project', 'application/x-project' // Additional MS Project MIME types
		];

		const newFiles = Array.from(e.target.files).filter(file => {
			if (file.size > maxFileSize) {
				toast.error(`${file.name} exceeds the 10 MB size limit.`,toastStyle());
				return false;
			}
			if (!allowedFileTypes.includes(file.type)) {
				toast.error(`${file.name} is not an allowed file type.`,toastStyle());
				return false;
			}
			return true;
		});

		const totalFiles = [...monthlyCostStatusEmailFormFiles, ...newFiles].slice(0, 15);
		dispatch(setMonthlyCostStatusEmailFormFiles(totalFiles));
	};
	
	const handleRemoveFile = (id) => {
		let copy_of_files = [...monthlyCostStatusEmailFormFiles];
		copy_of_files.splice(id, 1)
		dispatch(setMonthlyCostStatusEmailFormFiles(copy_of_files));
	};
	
	return (
		<Box display="flex" flexDirection="column" gap={3}>
			{(monthlyCostStatusData?.cost_status === 'Draft' || monthlyCostStatusData?.cost_status === 'Submitted' || hasAutosaved) && (
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Typography variant="subtitle1" className="titillium-web" gutterBottom style={{ color: '#003865' }}>
						Last updated on {new Date(monthlyCostStatusData?.last_updated_at).toLocaleString('en-US') || 'N/A'} by {monthlyCostStatusData?.last_updated_by || 'N/A'}
					{autoSaveStatus === 'saving' && <span style={{ color: '#888', fontStyle: 'italic', marginLeft: 8, fontSize: '0.85rem' }}>Saving...</span>}
					{autoSaveStatus === 'saved' && <span style={{ color: '#388e3c', marginLeft: 8, fontSize: '0.85rem' }}>Draft saved</span>}
					{autoSaveStatus === 'error' && <span style={{ color: '#d32f2f', marginLeft: 8, fontSize: '0.85rem' }}>Autosave failed</span>}
					</Typography>
					<Button
						className='smaller-primary-button'
						size="small"
						onClick={() => {
							const projectUrl = `${hubDomain}/phd/?tab=project&project_id=${selectedProjectId}`;
							window.open(projectUrl, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
						}}
						sx={{
							textTransform: 'none',
							fontSize: '0.875rem',
							padding: '4px 12px',
							borderColor: '#003865',
							color: '#003865',
							'&:hover': {
								borderColor: '#1976d2',
								backgroundColor: '#f5f5f5',
							}
						}}
						endIcon={<OpenInNewIcon
							sx={{
								fontSize: 'small',
								marginLeft: '-4px',
								}}
						/>}
					>
						Open Project for Reference
					</Button>
				</Box>
			)}
			<Box>
				<Typography variant="subtitle1" gutterBottom>
					Email Recipients:
				</Typography>
				<Box display="flex" flexWrap="wrap" gap={1} style={{ border: '1px solid #ccc', padding: '8px', borderRadius: '4px' }}>
					{emailRecipients.map((email) => (
						<Chip key={email} label={email} color="primary" variant="filled" sx={{
							padding: '2px 2px',
							fontSize: '0.8rem', 
							height: '24px',
						}}/>
					))}
					{/* Add help text here */}
				</Box>
				<Typography variant="caption" color="textSecondary">
					To edit the recipients, please go to the project settings.
				</Typography>
			</Box>

			{monthlyCostStatusData?.cost_status !== 'Submitted' && (
				<Box>
					<Typography variant="subtitle1" gutterBottom>
						Send Draft To Reviewer(s):
					</Typography>
					<Autocomplete
						multiple
						options={users || []}
						getOptionLabel={(option) => `${option.name} <${option.email}>`}
						filterSelectedOptions
						value={draftReviewers}
						onChange={(event, newValue) => setDraftReviewers(newValue)}
						isOptionEqualToValue={(option, value) => option.email === value.email}
						renderTags={(value, getTagProps) =>
							value.map((option, index) => (
								<Chip
									key={option.email}
									label={`${option.name} <${option.email}>`}
									style={{ margin: '2px', backgroundColor: '#093D61', color: 'white' }}
									deleteIcon={<HighlightOffIcon style={{ color: 'white' }} />}
									{...getTagProps({ index })}
								/>
							))
						}
						renderInput={(params) => (
							<TextField
								{...params}
								variant="outlined"
								label="Reviewers"
								placeholder="Type name or email"
							/>
						)}
					/>
					<FormHelperText>
						On "Save + Get Draft Email", a copy of the draft is emailed to these reviewers (e.g. your Director of Operations or Principal) alongside your own copy. They are not included in the final submission email. Your selection is remembered for next month.
					</FormHelperText>
				</Box>
			)}

			{/* Rendering the Financial Summary Table */}
			<MonthlyCostStatusFinancialSummary tableData={tableData} />

			{fields.map((field, idx) => (
				<Box key={field.id}>
					<Typography variant="subtitle1">{`${idx + 1}. ${field.label}`}</Typography>
					<EditorComponent
						fieldId={field.id}
						value={monthlyCostStatusEmailFormData?.[field.id]}
					/>
					{field.helperText && (
						<FormHelperText>{field.helperText}</FormHelperText>
					)}
				</Box>
			))}

			<Box>
				<Typography variant="subtitle2" gutterBottom>Attachments (max 15 files)</Typography>
				<Button
					variant="outlined"
					component="label"
					disabled={monthlyCostStatusEmailFormFiles.length >= 15}
				>
					Upload Files
					<input
						type="file"
						multiple
						hidden
						onChange={handleFileChange}
					/>
				</Button>
				<FormHelperText>Please attach the PDF export of your Procore Project Status Snapshot.</FormHelperText>

				<Stack spacing={1} mt={2}>
					{monthlyCostStatusEmailFormFiles.length > 0 && (
						<Box sx={{ width: '50%' }}>
							<ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
								{monthlyCostStatusEmailFormFiles.map((file, idx) => (
									<li
										key={idx}
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											border: '1px solid #e0e0e0',
											borderRadius: 8,
											padding: '8px 8px',
											marginBottom: 8,
											background: '#fafafa',
											boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
										}}
									>
										<Typography variant="body2" 
											sx={{
												maxWidth: 120,
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
												display: 'block',
											}}
										>{file.name}</Typography>
										<IconButton onClick={() => handleRemoveFile(idx)} size="small">
											<DeleteIcon fontSize="small" />
										</IconButton>
									</li>
								))}
							</ul>
						</Box>
					)}
				</Stack>

				{settingsData?.other_meta_data?.phd_sync && (
					<div>
						<br />
						<br />
						<Typography variant="body2" color="textSecondary" style={{ marginTop: '8px' }}>
							On clicking "Submit", the Cost Status Email will be sent and the Revised Contract Amount and Cost Status Profit will be synced with Salesforce.
						</Typography>
					</div>
				)}
			</Box>

			{/* Clear-all (kept low-prominence and away from Save/Submit) */}
			<Box display="flex" justifyContent="flex-end" mt={2}>
				<Button
					size="small"
					color="inherit"
					disabled={!clearButtonEnabled}
					onClick={() => setClearConfirmOpen(true)}
					sx={{ textTransform: 'none', color: clearButtonEnabled ? '#888' : undefined }}
				>
					Clear all cost status text
				</Button>
			</Box>

			<Dialog
				open={clearConfirmOpen}
				onClose={() => setClearConfirmOpen(false)}
				aria-labelledby="phd-clear-confirm-title"
			>
				<DialogTitle id="phd-clear-confirm-title">Clear all cost status text?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						This will erase all eight narrative fields in this form and cannot be undone.
						Files and snapshot selection are not affected.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setClearConfirmOpen(false)}>Cancel</Button>
					<Button onClick={handleClearAll} color="error" variant="contained">
						Clear text
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
